import Route from '@ember/routing/route';
import { set } from '@ember/object';

import { task, timeout } from 'ember-concurrency';
import { action } from '@ember/object';
import { service } from '@ember/service';

import { notifyUserError } from 'beebee-base/lib/utility';
import { beebeeAjaxGet } from 'beebee-base/lib/utility/ajax';

import { createPopper } from '@popperjs/core';

import getConfig from 'beebee-base/lib/get-config';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      route — app-shell bootstrap: anonymous session, cart rehydration from
 *              sessionStorage, org/dashboard load, and the delegated body-level
 *              click/mousedown/mouseup listeners that drive dropdown popups + the
 *              resizable slide-panel.
 *   extends:   Route (@ember/routing/route)
 *   template:  none
 *   model:     { params, dispos: { contacts: [], products: [] }, me } — me is
 *              session.me when authenticated, else {}.
 *   uses:      @popperjs/core createPopper (dropdown positioning, destroy-before-
 *              recreate in setPopperTask), lib/utility (notifyUserError), lib/utility/ajax
 *              (beebeeAjaxGet), lib/get-config
 *   siblings:  controllers/application.js (owns cart/loadDataTask this route seeds/
 *              kicks), application-route-test.js + application-route-binding-test.js
 *   tests:     application-route-test.js, application-route-binding-test.js
 *   gotchas:   normalizeCart() exists because a cart persisted by an older deploy (or
 *              corrupted) can be valid JSON but the wrong shape (null, non-object, a
 *              bucket that isn't an array) — application.totCart's `.reduce()` on a
 *              malformed bucket threw on EVERY route via the shell render. willDestroy
 *              explicitly removes the three delegated body listeners added in
 *              setBinding — this route instance persists for the app's lifetime but
 *              the listeners hold closures over `this`/the popper state, so they are
 *              torn down defensively rather than left to GC. loadAdvancedOpen tolerates
 *              a null `openData` (not yet loaded) so open_advanced + loadDataTask still
 *              run (Fixes EMBER-SCUOLE-ECOMMERCE-2XQ).
 *
 * Boots the whole storefront shell: session, cart rehydration, org data, and
 * the body-delegated UI chrome (dropdowns, resizable panel) shared by every route.
 */
export default class ApplicationRoute extends Route {
  @service intl;
  @service session;
  @service store;
  @service notify;
  @service log;

  urlApi = getConfig(this).APP.urlApi;
  urlPdfApi = getConfig(this).APP.urlPdfApi;
  urlFileUpload = getConfig(this).APP.urlFileUpload;
  urlData = getConfig(this).APP.urlData;
  urlSocket = getConfig(this).APP.urlSocket;

  async beforeModel(transition) {
    this.intl.setLocale(['en-US', 'en-GB', 'it-IT', 'fr-FR', 'de-DE']);

    const controller = this.controllerFor('application');
    this.session.setAnonymous(true);
    await this.session.bootstrapSession(transition, controller);
  }

  model(params) {
    return {
      params: params,
      dispos: {
        contacts: [],
        products: [],
      },
      me: this.session.isAuthenticated() ? this.session.me : {},
    };
  }

  setupController(controller, model) {
    if (controller.get('lang')) {
      controller.switchLangTask.perform(controller.get('lang'));
    }

    controller.updateBodyClasses();

    try {
      const beebeeEcommerceCart = sessionStorage.getItem('beebeeEcommerceCart');
      if (beebeeEcommerceCart) {
        set(controller, 'cart', this.normalizeCart(JSON.parse(beebeeEcommerceCart)));
      }
    } catch (e) {
      set(controller, 'cart', { groups: [], private: [], voucher: [] });
    }

    if (model.params?.ref) {
      const date = new Date();
      date.setTime(date.getTime() + 120 * 24 * 60 * 60 * 1000);
      const expires = date.toGMTString();
      document.cookie = `beebee_ref=${model.params.ref}; expires=${expires}`;
      set(model.params, 'ref', null);
    }

    controller.content = model;
    controller.urlApi = this.urlApi;
    controller.urlPdfApi = this.urlPdfApi;
    controller.urlSocket = this.urlSocket;
    controller.urlFileUpload = this.urlFileUpload;
    controller.urlData = this.urlData;

    this.loadAdvancedOpen.perform(controller);

    this.setBinding(controller);
  }

  // A cart persisted by an older deploy (or corrupted) may be valid JSON but the
  // wrong shape: a whole `null`, a non-object, or a bucket that is not an array.
  // `application.totCart` does `(this.cart.voucher || []).reduce(...)` for each
  // bucket, so a `null` cart threw "Cannot read properties of null" and an
  // object-valued bucket threw "reduce is not a function" — during the shell
  // render, i.e. on EVERY route, killing the app on load. Coerce anything that
  // is not a well-formed cart back into the empty-cart shape.
  normalizeCart(parsed) {
    const empty = () => ({ groups: [], private: [], voucher: [] });
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return empty();
    }
    const base = empty();
    return {
      groups: Array.isArray(parsed.groups) ? parsed.groups : base.groups,
      private: Array.isArray(parsed.private) ? parsed.private : base.private,
      voucher: Array.isArray(parsed.voucher) ? parsed.voucher : base.voucher,
    };
  }

  removeBodyLock() {
    const isOpenDropdown = document.querySelector('.data-dropdown.is-open');

    if (!isOpenDropdown) {
      document.body.classList.remove('dropdown-open');
    }
  }

  willDestroy() {
    super.willDestroy(...arguments);
    // Detach the delegated body listeners wired in setBinding.
    // `document.body` is null when teardown runs during document unload.
    if (this._bodyClickCaptureListener) {
      document.body?.removeEventListener('click', this._bodyClickCaptureListener, true);
      this._bodyClickCaptureListener = null;
    }
    if (this._bodyClickListener) {
      document.body?.removeEventListener('click', this._bodyClickListener);
      this._bodyClickListener = null;
    }
    if (this._bodyMousedownListener) {
      document.body?.removeEventListener('mousedown', this._bodyMousedownListener);
      this._bodyMousedownListener = null;
    }
    if (this._bodyMouseupListener) {
      document.body?.removeEventListener('mouseup', this._bodyMouseupListener);
      this._bodyMouseupListener = null;
    }
  }

  setBinding(controller) {
    const self = this;
    const applicationController = this.controllerFor('application');
    const resizeListener = this.resizeDiv.bind(this);
    const body = document.body;

    this._bodyClickListener = (e) => {
      const resizePanel = this.resizePanel;

      if (resizePanel) {
        document.removeEventListener('mousemove', resizeListener, false);
        body.classList.remove('disable-select');
        resizePanel.classList.remove('remove-animation');

        this.mousePos = null;
        this.resizePanel = null;
        applicationController.toggleProperty('resizeSlidePanel');
      }

      const target = e.target;
      // target can be a text node / document (no .closest) on some browsers.
      if (!(target instanceof Element)) {
        return;
      }
      // A handler that ran on the way up may have re-rendered the target out of
      // the document; its ancestors are then unreachable, so read them from the
      // capture-phase snapshot taken before any component handler ran.
      const snapshot =
        !target.isConnected && this._clickContext?.target === target ? this._clickContext : null;
      this._clickContext = null;

      const toggle = snapshot ? snapshot.toggle : target.closest('[data-toggle]');
      const ancle = snapshot
        ? snapshot.openDropdownAncestor
        : target.closest('.data-dropdown.is-open');

      if (toggle) {
        const toggleId = toggle.dataset.toggle;
        const pop = document.getElementById(toggleId);
        const isOpen = pop ? pop.classList.contains('is-open') : false;

        if (!isOpen) {
          if (!ancle) {
            document.querySelectorAll('.data-dropdown:not(.not-close)').forEach((element) => {
              element.classList.remove('is-open');
            });
          }
          if (pop) {
            pop.classList.add('is-open');
          }
          document.body.classList.add('dropdown-open');

          if (!controller.isSmallDevice) {
            this.setPopperTask.perform(toggle, pop);
          }
        } else {
          if (!ancle) {
            if (pop) {
              pop.classList.remove('is-open');
            }
            this.removeBodyLock();
          }
        }
      } else {
        if (!ancle) {
          const el = document.querySelector('.data-dropdown:not(.not-close)');
          if (el) {
            document.querySelectorAll('.data-dropdown:not(.not-close)').forEach((element) => {
              element.classList.remove('is-open');
            });
            this.removeBodyLock();
          }
        } else {
          const el = snapshot
            ? snapshot.closableDropdownAncestor
            : target.closest('.data-dropdown.is-open:not(.not-close)');
          if (el) {
            el.classList.remove('is-open');
            this.removeBodyLock();
          }
        }
      }
    };

    // Snapshot, in capture phase, the DOM context of the click. A control can
    // re-render itself away inside its own click handler, so the clicked node is
    // DETACHED by the time the bubble-phase listener above runs; closest() then
    // walks an orphan tree and returns null, and the click reads as "outside
    // every dropdown", closing the pane the user was still working in.
    if (this._bodyClickCaptureListener) {
      body.removeEventListener('click', this._bodyClickCaptureListener, true);
    }
    this._bodyClickCaptureListener = (e) => {
      const target = e.target;
      this._clickContext =
        target instanceof Element
          ? {
              target,
              toggle: target.closest('[data-toggle]'),
              openDropdownAncestor: target.closest('.data-dropdown.is-open'),
              closableDropdownAncestor: target.closest('.data-dropdown.is-open:not(.not-close)'),
            }
          : null;
    };

    // Il capture DEVE precedere il bubble.
    body.addEventListener('click', this._bodyClickCaptureListener, true);
    body.addEventListener('click', this._bodyClickListener);

    this._bodyMousedownListener = (e) => {
      const resizeSeparator =
        e.target instanceof Element ? e.target.closest('.resize-separator') : null;
      if (!resizeSeparator || !body.contains(resizeSeparator)) {
        return;
      }

      body.classList.add('disable-select');
      const parent = resizeSeparator.closest('.slide-panel');
      self.mousePos = e.x;
      self.resizePanel = parent;

      if (parent) {
        parent.classList.add('remove-animation');
      }

      document.addEventListener('mousemove', resizeListener, false);
    };

    this._bodyMouseupListener = (e) => {
      const resizeSeparator =
        e.target instanceof Element ? e.target.closest('.resize-separator') : null;
      if (!resizeSeparator || !body.contains(resizeSeparator)) {
        return;
      }

      document.removeEventListener('mousemove', resizeListener, false);
      body.classList.remove('disable-select');

      const resizePanel = self.resizePanel;

      if (resizePanel) {
        resizePanel.classList.remove('remove-animation');
      }

      self.mousePos = null;
      self.resizePanel = null;
      applicationController.toggleProperty('resizeSlidePanel');
    };

    body.addEventListener('mousedown', this._bodyMousedownListener);
    body.addEventListener('mouseup', this._bodyMouseupListener);
  }

  resizeDiv(e) {
    const panel = this.resizePanel;
    if (!panel) {
      return;
    }

    const rect = panel.getBoundingClientRect();
    const width = rect.width - (e.clientX - rect.left);

    panel.style.width = `${width}px`;

    const separator = panel.querySelector('.resize-separator');
    if (separator) {
      separator.style.right = `${width - 7}px`;
    }
  }

  @task *setPopperTask(ref, pop) {
    yield timeout(100);

    const applicationController = this.controllerFor('application');
    const refEl = ref;
    const popEl = pop;

    // Destroy the previous instance: each overwrite without destroy leaks a
    // popper (scroll/resize listeners) that keeps repositioning the popup.
    applicationController.popper?.destroy?.();

    const popper = createPopper(refEl, popEl, {
      placement: 'bottom-start',
      modifiers: [
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['bottom', 'right', 'top', 'left'],
          },
        },
      ],
    });

    set(applicationController, 'popper', popper);
  }

  @task({ drop: true }) *loadAdvancedOpen(controller) {
    yield timeout(150);

    try {
      const { openData, gdprField, openOrganization } = this.session;

      set(controller, 'gdprField', gdprField);
      set(controller, 'content.open_organization', openOrganization);

      // openData may be null (open-organization data not loaded yet); don't let
      // it abort the rest of the task — the shop still needs open_advanced +
      // loadDataTask below (Fixes EMBER-SCUOLE-ECOMMERCE-2XQ).
      const attributes = openData?.data?.attributes;
      if (attributes) {
        set(controller, 'content.copertina', {
          copertina_url: attributes.copertina_url,
          copertina_type: attributes.copertina_type,
          copertina_name: attributes.copertina_name,
          copertina_etag: attributes.copertina_etag,
        });
      }

      const getUrl = window.location;
      set(controller, 'loginUrl', getUrl.protocol + '//' + getUrl.host);

      const advanced = yield beebeeAjaxGet(
        `${getConfig(this).APP.urlApi}/organizations/open_advanced`
      );

      set(controller, 'content.open_advanced', advanced);

      controller.loadDataTask.perform();
    } catch (e) {
      this.log.error('Failed to load organization:', e);
      notifyUserError(this.notify, e, this.intl);
    }
  }

  @task({ restartable: true }) *animateTask() {
    yield timeout(50);

    document.querySelectorAll('.scroll-top-on-insert').forEach((el) => {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }

  @action
  willTransition(/*transition*/) {
    const applicationController = this.controllerFor('application');

    if (applicationController.windowsWidth <= 640) {
      set(applicationController, 'hideMenu', true);
    }

    set(applicationController, 'showCanvasAccount', false);
    set(applicationController, 'showCanvasNotification', false);
    set(applicationController, 'showCart', false);

    this.animateTask.perform();
  }

  @action
  error(reason /*, transition*/) {
    const application = this.controllerFor('application');

    if (reason && reason.errors) {
      reason.errors.forEach(function (error) {
        application.notify.error(error.title);
      });
    }

    return true;
  }
}
